import { getRiskRange } from '../utils/risk-profile';
import type { MinMaxEquityItem } from '../pages/ClientJourneyCommon/RiskToleranceStep';

export const createDoughnutOptions = (
    selectedRisk: string,
    t: any,
    minMaxEquity: Record<string, MinMaxEquityItem> = {},
    theme?: any
): Highcharts.Options => {
    const [start, end] = getRiskRange(selectedRisk, minMaxEquity);

    // Use semantic tokens if theme is provided, otherwise fall back to hardcoded values
    const disabledColor = theme?.palette?.semantic?.bg?.disabled || '#D4D5D8';
    const highlightColor = theme?.palette?.semantic?.text?.info || '#0D37BA';
    const borderColor = theme?.palette?.semantic?.bd?.inverse || '#FFFFFF';
    const textDefault = theme?.palette?.semantic?.text?.default || '#212121';
    const textMuted = theme?.palette?.semantic?.text?.muted || '#686B73';

    return {
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 300,
            events: {
                render: function () {
                    const chart = this as Highcharts.Chart;
                    const series = chart.series[0];
                    if (!series || !series.points.length) return;

                    if ((chart as any).customLabels) {
                        (chart as any).customLabels.forEach((lbl: any) => lbl.destroy());
                    }
                    (chart as any).customLabels = [];

                    const startPoint = series.points[0];
                    const startAngle = (startPoint.shapeArgs!.start + startPoint.shapeArgs!.end) / 2;
                    const r = (startPoint.shapeArgs!.r || 100) + 10;
                    const cx = chart.plotLeft + (startPoint.shapeArgs!.x || 0);
                    const cy = chart.plotTop + (startPoint.shapeArgs!.y || 0);
                    const startX = cx + r * Math.cos(startAngle);
                    const startY = cy + r * Math.sin(startAngle);

                    const startLabel = chart.renderer
                        .text(`${start}%`, startX - 15, startY - 35)
                        .css({ fontSize: '16px', fontWeight: 'bold', color: textDefault })
                        .add();
                    (chart as any).customLabels.push(startLabel);

                    const bluePoint = series.points[1];
                    const endAngle = bluePoint.shapeArgs!.end;
                    const endX = cx + r * Math.cos(endAngle);
                    const endY = cy + r * Math.sin(endAngle);

                    const endLabel = chart.renderer
                        .text(`${end}%`, endX - 10, endY - 5)
                        .css({ fontSize: '16px', fontWeight: 'bold', color: textDefault })
                        .add();
                    (chart as any).customLabels.push(endLabel);
                }
            }
        },
        title: {
            text: `
    <div style="text-align:center; margin-top: 30px">
      <span style="font-size:24px; font-weight:700; color:${textDefault}; font-family: 'TT Commons Pro';display: flex; flex-direction: column;">
        ${t(selectedRisk.toUpperCase())
            .split(' ')
            .map((word: string, index: number) => `<span key=${index}>${word}</span>`)
            .join('')}
      </span>
      <span style="font-size:16px; font-weight:400; color:${textMuted}; font-family: 'TT Commons Pro';">
        ${t('EQUITY_EXPOSURE')}
      </span>
    </div>`,
            useHTML: true,
            verticalAlign: 'middle',
            y: 89 - 15
        },
        tooltip: { enabled: false },
        credits: { enabled: false },
        plotOptions: {
            pie: {
                startAngle: -90,
                endAngle: 90,
                center: ['50%', '90%'],
                size: '140%',
                innerSize: '60%',
                dataLabels: { enabled: false },
                borderWidth: 0,
                enableMouseTracking: false,
                animation: false
            }
        },
        series: [
            {
                type: 'pie',
                borderWidth: 5,
                borderColor: borderColor,
                animation: false,
                data: [
                    { y: start, color: disabledColor },
                    { y: end - start, color: highlightColor },
                    { y: 100 - end, color: disabledColor }
                ]
            }
        ]
    };
};
